import { z } from 'zod';
import {
    CustomErrorParams,
    CustomErrorType,
    DateService,
    DateTimeUtcService,
    DateTimeUtils,
    ISOFormat,
    useAppNowUtc,
    useTimezone,
    Validation,
} from '@mews/framework';
import { Order } from '@optimus-web/core';

export enum FiltersValueKey {
    startDate = 'startDate',
    endDate = 'endDate',
    ownerId = 'ownerId',
    sort = 'sort',
}

const baseFilterSchema = z.object({
    [FiltersValueKey.startDate]: z.union([Validation.Date.Iso, z.literal('')]),
    [FiltersValueKey.endDate]: z.union([Validation.Date.Iso, z.literal('')]),
    [FiltersValueKey.ownerId]: z.string().nullish(),
});

export const createLedgerActivityReportFiltersSchema = (minEffectiveDate: string, timezone: string) => {
    return baseFilterSchema.superRefine((data, ctx) => {
        const todayDate = DateService.today(timezone);

        if (data.startDate) {
            const startDate = DateService.fromString(data.startDate, ISOFormat.Date, timezone);
            if (DateService.isGreater(startDate, todayDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    params: { errorType: CustomErrorType.DateString } satisfies CustomErrorParams,
                    fatal: true,
                    path: [FiltersValueKey.startDate],
                });
                return z.NEVER;
            }
        }
        if (data.endDate) {
            const endDate = DateService.fromString(data.endDate, ISOFormat.Date, timezone);
            if (DateService.isGreater(endDate, todayDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    params: { errorType: CustomErrorType.DateString } satisfies CustomErrorParams,
                    fatal: true,
                    path: [FiltersValueKey.endDate],
                });
                return z.NEVER;
            }
        }
        if (data.startDate && minEffectiveDate) {
            const startDate = DateService.fromString(data.startDate, ISOFormat.Date, timezone);
            const minEffectiveDateAsDate = DateTimeUtils.dateTimeUtcStringToDate(minEffectiveDate, timezone);

            if (DateService.isGreater(minEffectiveDateAsDate, startDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    params: { errorType: CustomErrorType.DateString } satisfies CustomErrorParams,
                    fatal: true,
                    path: [FiltersValueKey.startDate],
                });
                return z.NEVER;
            }
        }
        return data;
    });
};

export type LedgerActivityReportFilterValues = z.infer<typeof baseFilterSchema>;

export const urlSearchSchema = baseFilterSchema.partial().extend({
    [FiltersValueKey.sort]: z
        .record(z.string(), z.object({ order: z.nativeEnum(Order), priority: z.coerce.number() }))
        .optional(),
});

export type LedgerActivityReportUrlSearchValues = z.infer<typeof urlSearchSchema>;

export const useDefaultLedgerActivityReportFilterValues = () => {
    const appNowUtc = useAppNowUtc();
    const appPreviousDayUtc = DateTimeUtcService.addDays(appNowUtc, -1);

    const timezone = useTimezone();
    const appPreviousDayISO = DateTimeUtils.dateTimeUtcStringToDateISOString(appPreviousDayUtc.value, timezone);

    return {
        startDate: appPreviousDayISO,
        endDate: appPreviousDayISO,
        ownerId: '',
        sort: { accounted: { order: Order.Ascending, priority: 0 } },
    } satisfies LedgerActivityReportUrlSearchValues;
};
